import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, ArrowRight, CheckCircle, Users, Calendar,
  BookOpen, Briefcase, Award, FileText, Database,
  BarChart3, Shield, Globe, Zap, ChevronDown,
  Play, School, Building2, FlaskConical
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
  GSAPCounter,
} from '@/components/ui/OptimizedAnimations';
import {
  CharacterStagger,
  SparklesCore,
  Spotlight,
} from '@/components/ui/AceternityAnimations';

const features = [
  {
    icon: Award,
    title: 'NEP 2020 Compliance',
    description: 'Full compliance with National Education Policy 2020 including credit transfer, multiple entry-exit, and outcome-based education.',
  },
  {
    icon: FlaskConical,
    title: 'Research Portal',
    description: 'Track publications, patents, grants, and collaborations. Generate impact reports and manage research workflows.',
  },
  {
    icon: Briefcase,
    title: 'Placement Module',
    description: 'End-to-end placement management with company portal, resume builder, interview scheduling, and analytics.',
  },
  {
    icon: Users,
    title: 'Alumni Network',
    description: 'Engage alumni with networking features, mentorship programs, and donation tracking.',
  },
  {
    icon: FileText,
    title: 'Faculty Portal',
    description: 'Comprehensive faculty management including workload, publications, leave, and performance tracking.',
  },
  {
    icon: BookOpen,
    title: 'Library System',
    description: 'Digital library management with e-resources, OPAC, and integration with academic databases.',
  },
];

const nepModules = [
  { name: 'Academic Bank of Credits', desc: 'ABC integration for credit transfer' },
  { name: 'Outcome Based Education', desc: 'CO-PO mapping and attainment' },
  { name: 'NAAC/NBA Compliance', desc: 'Automated data collection and SSR' },
  { name: 'Multiple Entry-Exit', desc: 'Flexible degree pathways' },
];

const stats = [
  { value: '150', suffix: '+', label: 'Universities' },
  { value: '1', suffix: 'M+', label: 'Students' },
  { value: '40', suffix: '%', label: 'Placement Increase' },
  { value: '99.9', suffix: '%', label: 'Uptime' },
];

const accreditations = [
  { name: 'NAAC', desc: 'National Assessment and Accreditation Council' },
  { name: 'NBA', desc: 'National Board of Accreditation' },
  { name: 'NIRF', desc: 'National Institutional Ranking Framework' },
  { name: 'AISHE', desc: 'All India Survey on Higher Education' },
];

const faqs = [
  {
    question: 'Is the system compliant with NEP 2020?',
    answer: 'Yes, our College ERP is fully compliant with NEP 2020 guidelines including Academic Bank of Credits, multiple entry-exit options, and outcome-based education frameworks.',
  },
  {
    question: 'Can it help with NAAC accreditation?',
    answer: 'Absolutely. The system automatically collects and organizes data required for NAAC SSR and DVV. Many universities have reported significant time savings in accreditation preparation.',
  },
  {
    question: 'How does the placement module work?',
    answer: 'Companies register on the portal, post opportunities, and schedule interviews. Students build profiles, apply for positions, and track their applications. Placement officers get real-time dashboards and reports.',
  },
  {
    question: 'Is training provided for faculty and staff?',
    answer: 'Yes, we provide comprehensive training programs for all user types. Our support team remains available for ongoing assistance and we provide detailed documentation.',
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <motion.div className="glass-card overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full p-6 flex items-center justify-between text-left hover:bg-glass-hover transition-colors"
    >
      <span className="text-foreground font-medium pr-4">{faq.question}</span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronDown className="w-5 h-5 text-orange flex-shrink-0" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-6 pb-6 text-muted-foreground">
            {faq.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const CollegeERPPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="hsl(16, 100%, 60%)" />

        <div className="absolute inset-0 z-[1]">
          <SparklesCore
            id="collegeErpSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={30}
            particleColor="#FF6B35"
          />
        </div>

        <FloatingElement duration={8} distance={20} className="absolute top-20 right-10 opacity-10">
          <div className="w-64 h-64 border border-orange/30 rounded-full" />
        </FloatingElement>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal delay={0.1}>
                <Link to="/products" className="inline-flex items-center gap-2 text-orange text-sm mb-6 hover:underline">
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back to Products
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-orange/20 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-orange" />
                  </div>
                  <span className="px-3 py-1 rounded-full bg-orange/10 text-orange text-xs font-mono">
                    Universities & Colleges
                  </span>
                </div>
              </ScrollReveal>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-foreground mb-4">
                <CharacterStagger
                  text="College ERP"
                  delay={0.3}
                  staggerDelay={0.03}
                />
              </h1>
              <h2 className="text-2xl sm:text-3xl font-display text-gradient-orange mb-6">
                Higher Education Suite
              </h2>

              <ScrollReveal delay={0.6}>
                <p className="text-muted-foreground text-lg mb-8 max-w-xl">
                  Comprehensive university management with NEP 2020 compliance, NAAC/NBA accreditation support,
                  and advanced placement automation. Trusted by 150+ institutions globally.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.8}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <MagneticButton strength={0.2}>
                    <Link to="/contact">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-orange text-primary-foreground px-8 py-4 rounded-full font-medium flex items-center gap-2 shadow-orange-glow"
                      >
                        Book a Demo
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>
                    </Link>
                  </MagneticButton>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-secondary px-8 py-4"
                  >
                    <Play className="w-5 h-5" />
                    Watch Overview
                  </motion.button>
                </div>
              </ScrollReveal>
            </div>

            {/* Stats Card */}
            <ScrollReveal direction="right" delay={0.4}>
              <div className="glass-card p-8 border-orange/20">
                <h3 className="text-display text-xl text-foreground mb-6">
                  Empowering Higher Education
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-3xl font-display font-bold text-orange">
                        <GSAPCounter value={stat.value} suffix={stat.suffix} duration={2} />
                      </div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* NEP 2020 Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal direction="left">
              <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
                Policy Compliant
              </span>
              <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                Built for<br />
                <span className="text-gradient-orange">NEP 2020</span>
              </h2>
              <p className="text-muted-foreground mb-8">
                Our College ERP is designed from the ground up to support the National Education Policy 2020.
                From Academic Bank of Credits to outcome-based education, we&apos;ve got you covered.
              </p>
              <div className="space-y-4">
                {nepModules.map((module, index) => (
                  <motion.div
                    key={module.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-orange mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-foreground font-medium">{module.name}</h4>
                      <p className="text-sm text-muted-foreground">{module.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right">
              <div className="grid grid-cols-2 gap-4">
                {accreditations.map((acc, index) => (
                  <motion.div
                    key={acc.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -4 }}
                    className="glass-card p-6 text-center border-orange/20 hover:border-orange/40 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-orange/20 flex items-center justify-center mx-auto mb-3">
                      <Award className="w-6 h-6 text-orange" />
                    </div>
                    <h4 className="text-display text-lg text-foreground mb-1">{acc.name}</h4>
                    <p className="text-xs text-muted-foreground">{acc.desc}</p>
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Core Features
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Comprehensive Higher Education<br />
              <span className="text-gradient-orange">Management Platform</span>
            </h2>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <StaggerItem key={feature.title}>
                <InteractiveCard hoverScale={1.02} hoverY={-6} glowColor="rgba(255,107,53,0.2)">
                  <div className="glass-card p-6 h-full border-orange/10 hover:border-orange/30 transition-colors">
                    <div className="w-12 h-12 rounded-xl bg-orange/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-orange" />
                    </div>
                    <h3 className="text-display text-lg text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Placement Highlight Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 md:p-12 border-orange/20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <ScrollReveal>
                  <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
                    Placement Module
                  </span>
                  <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                    40% Average Increase in<br />
                    <span className="text-gradient-orange">Placement Rates</span>
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Our placement module connects students with top recruiters, automates the entire
                    hiring process, and provides actionable analytics to improve outcomes year over year.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {[
                      'Company registration and job posting portal',
                      'AI-powered resume builder for students',
                      'Interview scheduling and feedback tracking',
                      'Real-time placement analytics dashboard',
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-orange flex-shrink-0" />
                        <span className="text-foreground text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollReveal>
              </div>
              <ScrollReveal direction="right">
                <div className="grid grid-cols-2 gap-4">
                  <div className="glass-card p-6 text-center">
                    <div className="text-4xl font-display font-bold text-orange mb-2">500+</div>
                    <div className="text-sm text-muted-foreground">Companies Onboarded</div>
                  </div>
                  <div className="glass-card p-6 text-center">
                    <div className="text-4xl font-display font-bold text-orange mb-2">85%</div>
                    <div className="text-sm text-muted-foreground">Student Placement</div>
                  </div>
                  <div className="glass-card p-6 text-center">
                    <div className="text-4xl font-display font-bold text-orange mb-2">12 LPA</div>
                    <div className="text-sm text-muted-foreground">Average Package</div>
                  </div>
                  <div className="glass-card p-6 text-center">
                    <div className="text-4xl font-display font-bold text-orange mb-2">48 hrs</div>
                    <div className="text-sm text-muted-foreground">Avg. Response Time</div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-void">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Common Questions
            </span>
            <h2 className="text-display text-3xl text-foreground">
              Frequently Asked <span className="text-gradient-orange">Questions</span>
            </h2>
          </ScrollReveal>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <FAQItem
                  faq={faq}
                  isOpen={openFaq === index}
                  onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
              Ready to Modernize Your<br />
              <span className="text-gradient-orange">University?</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Join 150+ universities that have transformed their operations with our College ERP.
              Schedule a demo to see how we can help with NEP compliance and accreditation.
            </p>
            <MagneticButton strength={0.2}>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-orange text-primary-foreground px-10 py-5 rounded-full font-medium text-lg flex items-center gap-2 mx-auto shadow-orange-glow"
                >
                  Book Your Free Demo
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </MagneticButton>
          </ScrollReveal>
        </div>
      </section>

      {/* Explore Suite Section */}
      <section className="py-16 bg-void border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-8">
            <h3 className="text-display text-xl text-foreground">
              Explore Our <span className="text-gradient-teal">Suite</span>
            </h3>
          </ScrollReveal>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { name: 'School ERP', icon: School, href: '/products/school-erp', color: 'teal' },
              { name: 'Lynkt', icon: Users, href: '/products/lynkt', color: 'teal' },
              { name: 'LMS', icon: BookOpen, href: '/products/lms', color: 'orange' },
            ].map((product) => (
              <Link key={product.name} to={product.href}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  className={cn(
                    'glass-card p-6 flex items-center gap-4 cursor-pointer',
                    'border transition-colors duration-300',
                    product.color === 'teal' ? 'hover:border-teal/40' : 'hover:border-orange/40'
                  )}
                >
                  <div className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center',
                    product.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                  )}>
                    <product.icon className={cn(
                      'w-6 h-6',
                      product.color === 'teal' ? 'text-teal' : 'text-orange'
                    )} />
                  </div>
                  <div>
                    <h4 className="text-display text-foreground">{product.name}</h4>
                    <p className="text-xs text-muted-foreground">Explore product</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CollegeERPPage;
